/* ============================================================
   SAM-714 — event preview (/eventsad/:eventId/preview, /eventsad/new/preview).

   Renders the public event detail exactly as capcana.app does
   (PublicEventDetailView, the same component PublicEventDetail renders) but fed
   with the editor's data instead of the public API — so drafts, which the
   public endpoint never returns, can be checked before publishing.

   - Desktop / Phone toggle. Phone is a 390px frame using the mobile banner
     (falling back to the desktop one, like the public page); Desktop uses the
     desktop banner (background_img_url).
   - Data: the editor passes its current form in router state (unsaved edits
     included). Opened directly by URL, the saved event is loaded instead.
   - Edit goes back to the editor with the same data; Publish (approvers, drafts
     only) saves with status=published.
   ============================================================ */
import { pickEventHero } from '../../utils/eventHero';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Monitor, Pencil, Send, Smartphone } from 'lucide-react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useEvent, useEventMutations, useEventTypes, eventStatusOf } from '../../hooks/useEvents';
import { isPrivateEventTypeId } from '../../utils/privateEvent';
import { PublicEventDetailView } from '../public/PublicEventDetail';
import { PublicLangProvider } from '../../i18n/publicEvents';
import { toaster } from '../../components/ui/toast';
import {
  createdEventId, eventToForm, formToPayload, formToPreviewEvent, missingFieldsHint, missingRequiredFields,
  type EventForm,
} from './eventForm';
import { EventStatusBadge } from './EventStatusBadge';
import type { EventDraftState } from './EventEditorPage';

type Device = 'desktop' | 'phone';

export const EventPreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams<{ eventId?: string }>();
  const isNew = !eventId || eventId === 'new';
  const draft = (location.state as EventDraftState | null)?.draft;

  const { isEventApprover } = useUserPermissions();
  const { data: event, isLoading } = useEvent(isNew || draft ? undefined : eventId);
  const { createEvent, updateEvent, isCreating, isUpdating } = useEventMutations();
  const [device, setDevice] = useState<Device>('desktop');
  const { data: eventTypes } = useEventTypes();

  const form: EventForm | null = useMemo(() => {
    if (draft) return draft;
    if (event) return eventToForm(event, eventStatusOf(event));
    return null;
  }, [draft, event]);

  const editPath = `/eventsad/${isNew ? 'new' : eventId}`;
  const goEdit = () => navigate(editPath, { state: draft ? ({ draft } satisfies EventDraftState) : null });

  if (!form) {
    if (isNew) {
      // Nothing to preview without the editor's data.
      return (
        <div className="p-8 text-sm" style={{ color: 'var(--ink-500)' }}>
          <p>Nothing to preview yet.</p>
          <button type="button" className="btn ghost mt-3" onClick={() => navigate('/eventsad/new')}>
            <ArrowLeft size={14} /> New event
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-full text-sm" style={{ color: 'var(--ink-500)' }}>
        {isLoading ? <><Loader2 className="animate-spin mr-2" size={16} /> Loading event…</> : 'Could not load this event.'}
      </div>
    );
  }

  const status = form.status;
  const isPrivate = isPrivateEventTypeId(form.event_type_id, eventTypes);
  const missing = missingRequiredFields(form);
  const label = status === 'draft'
    ? 'Draft · only you can see this'
    : status === 'published'
      ? 'Published · visible on capcana.app'
      : 'Archived · not visible on capcana.app';
  const showPublish = isEventApprover && status === 'draft';
  const isSaving = isCreating || isUpdating;

  const publish = async () => {
    if (missing.length) {
      toaster.negative(missingFieldsHint(missing, 'publish'), {});
      return;
    }
    try {
      const payload = formToPayload(form, 'published');
      if (isNew) {
        const id = createdEventId(await createEvent(payload));
        navigate(id ? `/eventsad/${id}` : '/eventsad/list', { replace: true });
      } else {
        await updateEvent({ id: eventId!, data: payload });
        navigate(`/eventsad/${eventId}`, { replace: true, state: null });
      }
    } catch {
      // useEventMutations already shows the error toast.
    }
  };

  const previewEvent = formToPreviewEvent(form, isNew ? undefined : eventId, isPrivate);
  const hero = pickEventHero(form, device === 'phone'); // the same pick as capcana.app

  const seg = (on: boolean): React.CSSProperties => ({
    border: 'none', borderRadius: 'var(--r-8)', padding: '6px 12px', cursor: 'pointer',
    background: on ? '#fff' : 'transparent', color: on ? 'var(--ink-900)' : 'var(--ink-500)',
    boxShadow: on ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
  });

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--surface)' }}>
      <header
        className="flex items-center gap-3 px-6 py-3 shrink-0 bg-white flex-wrap"
        style={{ borderBottom: '1px solid var(--line)', position: 'relative', zIndex: 10 }}
      >
        <button
          type="button"
          onClick={goEdit}
          aria-label="Back to the editor"
          style={{
            width: 36, height: 36, borderRadius: 'var(--r-8)', border: '1px solid var(--line)', borderStyle: 'solid',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fff',
            color: 'var(--ink-700)', flexShrink: 0, cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-[18px] font-semibold truncate m-0" style={{ color: 'var(--ink-900)' }}>
            Preview · {form.name_en || form.name_es || 'New event'}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <EventStatusBadge status={status} />
            <span className="text-xs" style={{ color: 'var(--ink-500)' }}>{label}</span>
          </div>
        </div>

        <div role="group" aria-label="Device" className="flex p-0.5 text-[12px] font-semibold" style={{ background: 'var(--line-subtle)', borderRadius: 'var(--r-8)' }}>
          <button type="button" aria-pressed={device === 'desktop'} onClick={() => setDevice('desktop')} className="inline-flex items-center gap-1.5" style={seg(device === 'desktop')}>
            <Monitor size={13} /> Desktop
          </button>
          <button type="button" aria-pressed={device === 'phone'} onClick={() => setDevice('phone')} className="inline-flex items-center gap-1.5" style={seg(device === 'phone')}>
            <Smartphone size={13} /> Phone
          </button>
        </div>

        <button type="button" className="btn ghost" onClick={goEdit}>
          <Pencil size={14} /> Edit
        </button>
        {showPublish && (
          <button type="button" className="btn primary" onClick={publish} disabled={isSaving} title={missingFieldsHint(missing, 'publish') || undefined}>
            <Send size={14} /> {isSaving ? 'Publishing…' : 'Publish'}
          </button>
        )}
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        <div
          className="mx-auto bg-white overflow-y-auto"
          style={{
            width: device === 'phone' ? 390 : '100%',
            maxWidth: device === 'phone' ? 390 : 1200,
            height: device === 'phone' ? 844 : 'calc(100vh - 160px)',
            minHeight: 480,
            border: '1px solid var(--line)',
            borderRadius: device === 'phone' ? 36 : 'var(--r-12)',
            boxShadow: '0 8px 24px rgba(17,24,39,0.08)',
          }}
        >
          <PublicLangProvider>
            <PublicEventDetailView
              event={previewEvent}
              heroImageKey={hero.key}
              heroFullImage={hero.full}
              onBack={goEdit}
              onRegister={() => toaster.info('Registration is disabled in the preview', { autoHideDuration: 2000 })}
              pageStyle={{ minHeight: '100%' }}
            />
          </PublicLangProvider>
        </div>
      </div>
    </div>
  );
};

export default EventPreviewPage;
